import { useEffect, useState } from 'react';

import { useVisibilityGate } from '@/lib/visibility-gate';

import type { RefObject } from 'react';

/**
 * One driver for every `/features` showcase visual: a step counter that
 * advances on a beat, pauses offscreen, and never starts at all under reduced
 * motion.
 *
 * ## Why one driver rather than four
 *
 * Each visual on this page is a small scripted sequence — a scan sweeping a
 * page, a confirmation being approved, capabilities being switched on. Written
 * separately that is four `setInterval`s, four gate wirings and four
 * reduced-motion branches, which is four places for the landing page's own
 * measured defect to come back: drivers that ran from mount forever, scrolled
 * past, in a backgrounded tab, for the whole session.
 *
 * With the beat hoisted, every visual becomes a PURE FUNCTION of `step`. That
 * is the property that makes the rest cheap: reduced motion is not a separate
 * rendering path, it is the same render pinned to the last step.
 *
 * ## Reduced motion lands on the LAST step, not the first
 *
 * The first step of each of these sequences is an empty state — a page nobody
 * has scanned, a chat nobody has answered — so freezing there would show a
 * visitor who asked for less motion an empty box and call it a feature
 * showcase. The last step is the finished picture, which is exactly what the
 * still frame should be. Same discipline as the hero mark's near-zero
 * durations, for the same reason.
 *
 * ## SSG
 *
 * `useState`'s initial value is what the prerender emits, so the static HTML
 * carries the FINISHED frame too. A crawler, a no-JS reader and a
 * reduced-motion visitor all see the same complete picture, and hydration
 * rewinds to step 0 only when the sequence is actually going to play.
 */
export interface SteppedLoop<T extends Element> {
  /** Attach to the visual's root — it is what the gate observes. */
  readonly ref: RefObject<T>;
  /** Current step, `0 … stepCount - 1`. */
  readonly step: number;
}

export function useSteppedLoop<T extends Element>(
  stepCount: number,
  beatMs: number,
): SteppedLoop<T> {
  const { ref, active } = useVisibilityGate<T>();
  // Start on the finished frame — see the SSG note above.
  const [step, setStep] = useState(stepCount - 1);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    // Rewind to the empty state exactly once, on the first frame after
    // hydration, so the sequence has somewhere to play FROM.
    setStep(0);
    return undefined;
  }, [stepCount]);

  useEffect(() => {
    if (!active) return undefined;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    const id = window.setInterval(() => {
      // A pure updater: the next step is a function of the current one and
      // nothing else. Anything with a side effect belongs outside it — React
      // may invoke an updater more than once, so a write in here fires more
      // than once too, silently and without a warning.
      setStep((current) => (current + 1) % stepCount);
    }, beatMs);
    return () => {
      window.clearInterval(id);
    };
  }, [active, beatMs, stepCount]);

  return { ref, step };
}
